import React, { useState, Component } from 'react';
import { Send, CheckCircle } from 'lucide-react';
const CONSULTATION_EMAIL = 'hello@nexoraresume.com';
export function Contact() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    target: 'Board Appointment',
    message: ''
  });
  const update = (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => setForm(prev => ({
    ...prev,
    [field]: e.target.value
  }));
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormState('submitting');
    // Open the visitor's email client with a pre-filled consultation request
    // addressed to the firm.
    const subject = `Consultation Request — ${form.firstName} ${form.lastName}`.trim();
    const body = [`Name: ${form.firstName} ${form.lastName}`, `Email: ${form.email}`, `Primary Goal: ${form.target}`, '', 'Background:', form.message || '(none provided)'].join('\n');
    const mailto = `mailto:${CONSULTATION_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailto;
    setFormState('success');
  };
  return <section id="contact" className="py-24 bg-charcoal-900 relative overflow-hidden" data-id="element-75">
      <div className="absolute top-0 right-0 w-1/2 h-full bg-navy-900 transform skew-x-12 translate-x-32 hidden lg:block" data-id="element-76"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-77">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center" data-id="element-78">
          <div data-id="element-79">
            <h2 className="text-4xl md:text-5xl font-serif-display font-bold text-white mb-6 leading-tight" data-id="element-80">
              Ready for Your Next Board Seat or Executive Opportunity?
            </h2>
            <p className="text-gray-300 text-lg mb-10 leading-relaxed" data-id="element-81">
              Schedule a confidential consultation to discuss your career
              trajectory and how we can position you for the highest levels of
              corporate leadership.
            </p>

            <div className="space-y-6 text-gray-300" data-id="element-82">
              <div className="flex items-center" data-id="element-83">
                <div className="w-12 h-12 bg-navy-800 rounded-sm flex items-center justify-center mr-4 border border-navy-700" data-id="element-84">
                  <span className="text-gold font-bold" data-id="element-85">01</span>
                </div>
                <div data-id="element-86">
                  <h4 className="text-white font-bold" data-id="element-87">Submit Inquiry</h4>
                  <p className="text-sm" data-id="element-88">
                    Provide your current details and goals.
                  </p>
                </div>
              </div>
              <div className="flex items-center" data-id="element-89">
                <div className="w-12 h-12 bg-navy-800 rounded-sm flex items-center justify-center mr-4 border border-navy-700" data-id="element-90">
                  <span className="text-gold font-bold" data-id="element-91">02</span>
                </div>
                <div data-id="element-92">
                  <h4 className="text-white font-bold" data-id="element-93">Executive Review</h4>
                  <p className="text-sm" data-id="element-94">Our team reviews your background.</p>
                </div>
              </div>
              <div className="flex items-center" data-id="element-95">
                <div className="w-12 h-12 bg-navy-800 rounded-sm flex items-center justify-center mr-4 border border-navy-700" data-id="element-96">
                  <span className="text-gold font-bold" data-id="element-97">03</span>
                </div>
                <div data-id="element-98">
                  <h4 className="text-white font-bold" data-id="element-99">Strategy Session</h4>
                  <p className="text-sm" data-id="element-100">We discuss your branding strategy.</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white p-8 md:p-10 rounded-sm shadow-2xl" data-id="element-101">
            {formState === 'success' ? <div className="text-center py-12" data-id="element-102">
                <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-6" data-id="element-103" />
                <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-104">
                  Inquiry Received
                </h3>
                <p className="text-gray-600" data-id="element-105">
                  Your email draft to{' '}
                  <span className="font-semibold text-navy-900" data-id="element-106">
                    hello@nexoraresume.com
                  </span>{' '}
                  is ready in your mail app — just hit send. A senior branding
                  strategist will respond within 24 hours. If nothing opened,
                  email us directly at that address.
                </p>
                <button onClick={() => setFormState('idle')} className="mt-8 text-gold font-semibold hover:text-gold-600 transition-colors" data-id="element-107">
                  Submit another inquiry
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-108">
                <h3 className="text-2xl font-bold text-navy-900 mb-6 font-serif-display" data-id="element-109">
                  Request Consultation
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-110">
                  <div data-id="element-111">
                    <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-112">
                      First Name
                    </label>
                    <input type="text" id="firstName" required value={form.firstName} onChange={update('firstName')} className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none transition-all" data-id="element-113" />
                  </div>
                  <div data-id="element-114">
                    <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-115">
                      Last Name
                    </label>
                    <input type="text" id="lastName" required value={form.lastName} onChange={update('lastName')} className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none transition-all" data-id="element-116" />
                  </div>
                </div>

                <div data-id="element-117">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-118">
                    Corporate Email
                  </label>
                  <input type="email" id="email" required value={form.email} onChange={update('email')} className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none transition-all" data-id="element-119" />
                </div>

                <div data-id="element-120">
                  <label htmlFor="target" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-121">
                    Primary Goal
                  </label>
                  <select id="target" value={form.target} onChange={update('target')} className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none transition-all bg-white" data-id="element-122">
                    <option data-id="element-123">Board Appointment</option>
                    <option data-id="element-124">C-Suite Transition</option>
                    <option data-id="element-125">Executive Promotion</option>
                    <option data-id="element-126">LinkedIn Optimization</option>
                    <option data-id="element-127">Other</option>
                  </select>
                </div>

                <div data-id="element-128">
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-129">
                    Brief Background (Optional)
                  </label>
                  <textarea id="message" rows={4} value={form.message} onChange={update('message')} className="w-full px-4 py-3 border border-gray-300 rounded-sm focus:ring-2 focus:ring-gold focus:border-gold outline-none transition-all resize-none" data-id="element-130"></textarea>
                </div>

                <button type="submit" disabled={formState === 'submitting'} className="w-full bg-navy-900 hover:bg-navy-800 text-white px-6 py-4 rounded-sm font-semibold text-lg transition-all duration-300 flex items-center justify-center group disabled:opacity-70" data-id="element-131">
                  {formState === 'submitting' ? 'Processing...' : 'Get Started Today'}
                  {formState === 'idle' && <Send className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-132" />}
                </button>
                <p className="text-xs text-gray-500 text-center mt-4" data-id="element-133">
                  All inquiries are strictly confidential.
                </p>
              </form>}
          </div>
        </div>
      </div>
    </section>;
}